<script setup lang="ts">
import type { Component } from "vue";
import { computed, onBeforeMount, onMounted, useTemplateRef, watch } from "vue";
import "vue-sonner/style.css";

import { tabVisibilityStateChanged } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import ArtObjectivesBoard from "@/components/board/ArtObjectivesBoard.vue";
import BacklogBoard from "@/components/board/BacklogBoard.vue";
import FlexBoard from "@/components/board/FlexBoard.vue";
import ProgramBoard from "@/components/board/ProgramBoard.vue";
import RiskBoard from "@/components/board/RiskBoard.vue";
import SolutionBacklogBoard from "@/components/board/SolutionBacklogBoard.vue";
import SolutionBoard from "@/components/board/SolutionBoard.vue";
import TeamBoard from "@/components/board/TeamBoard.vue";
import LinkDragLine from "@/components/linkDrag/LinkDragLine.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import SkipButton from "@/components/ui/a11y/SkipButton.vue";
import { useNativeEvents } from "@/composables/useNativeEvents";
import type { BoardType } from "@/model/baseTypes";
import { useActivityStore } from "@/store/activity";
import { useBoardStore } from "@/store/board";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useDrawStore } from "@/store/draw";
import { usePanModeStore } from "@/store/panMode";
import { useSessionStore } from "@/store/session";
import { useToastStore } from "@/store/toast";
import { useZoomStore } from "@/store/zoom";
import { clearCache } from "@/utils/text/fontSizeCache";

import ConnectionStatus from "./internal/ConnectionStatus.vue";
import PaintLayer from "./internal/DefaultPaintLayer.vue";
import DrawLayer from "./internal/DrawLayer/DrawLayer.vue";
import MenuContainer from "./internal/MenuContainer/MenuContainer.vue";
import MousePointers from "./internal/MousePointers/MousePointers.vue";
import SelectionLayer from "./internal/SelectionLayer/SelectionLayer.vue";
import ToastMessage from "./internal/ToastMessage/ToastMessage.vue";
import DefaultZoomLayer from "./internal/ZoomLayer/DefaultZoomLayer.vue";
import { zoomHandler } from "./internal/ZoomLayer/ZoomHandler";

const zoomLayerRef = useTemplateRef("zoomLayer");
const boardRef = useTemplateRef<{ focus?: () => void }>("board");

let zoomer: ReturnType<typeof zoomHandler> | null = null;

const { addEventListener } = useNativeEvents();

// temp solution to fix the font size cache issue
onBeforeMount(clearCache);

onMounted(() => {
  zoomer = zoomHandler(zoomLayerRef.value!);
  addEventListener(document, "visibilitychange", () =>
    trackEvent(tabVisibilityStateChanged(document.visibilityState)),
  );
});

const boardComponent = computed(() =>
  useSessionStore().isSessionLoading ? null : boardFor(boardType.value),
);
const boardType = computed(() => useBoardStore().currentBoard().type);
const isZooming = computed(() => useZoomStore().zooming);
const showDrawLayer = computed(() => {
  const isZoomActive = isZooming.value && useZoomStore().useZoomLayer;
  return !isZoomActive && useDrawStore().isSupportedBoard(boardType.value);
});
const canUsePanMode = computed(() => usePanModeStore().active);
const toast = computed(() => useToastStore().toast);

watch(isZooming, (zooming) => {
  if (zooming) {
    zoomer?.startZoom();
  } else {
    zoomer?.endZoom();
  }
});

// Optional mode to meet accessibility standards
watch(
  () => useClientSettingsStore().highContrastMode,
  (useMode, oldValue) => {
    if (useMode === oldValue || !document?.body) return;

    // Apply to the body so it also affects dropdown menus
    if (useMode) {
      document.body.classList.add("high-contrast-mode");
    } else {
      document.body.classList.remove("high-contrast-mode");
    }
  },
  { immediate: true },
);

watch(
  () => useBoardStore().board,
  useActivityStore().inactivate, // close activity panel on board change
);

function boardFor(type: BoardType): Component {
  switch (type) {
    case "backlog":
      return BacklogBoard;
    case "program":
      return ProgramBoard;
    case "solution_backlog":
      return SolutionBacklogBoard;
    case "solution":
      return SolutionBoard;
    case "risk":
      return RiskBoard;
    case "team":
      return TeamBoard;
    case "flex":
      return FlexBoard;
    case "objectives":
      return ArtObjectivesBoard;
  }
}

function closeToast() {
  useToastStore().hide(true);
}
</script>

<template>
  <div>
    <ConnectionStatus />
    <SkipButton @click="boardRef?.focus">
      {{ $t("label.skipToBoard") }}
    </SkipButton>
    <ToastMessage v-bind="toast" @close="closeToast" />
    <MenuContainer />
    <MousePointers />
    <LinkDragLine />
    <SvgIcon name="reaction/defs" />
    <div id="boards" class="boards">
      <div id="boards-border" />
      <SelectionLayer />
      <DrawLayer v-if="showDrawLayer" />
      <DefaultZoomLayer ref="zoomLayer" />
      <PaintLayer />
      <KeepAlive v-if="!!boardComponent" role="tabpanel" :aria-labelledby="boardType + '_tab'">
        <component
          :is="boardComponent"
          :id="boardType + '_panel'"
          ref="board"
          :class="{ 'pan-mode': canUsePanMode }"
        />
      </KeepAlive>
    </div>
  </div>
</template>

<style lang="scss">
@use "@/styles/font";
@use "@/styles/variables";
@use "@/styles/board";
@use "@/styles/variables/colors";
@use "@/styles/variables/a11y" as *; // for high-contrast mode

.boards {
  transform-origin: top left;
  position: absolute;

  #boards-border {
    position: absolute;
    left: variables.$fake-zoom * 100%;
    top: variables.$fake-zoom * 100%;
    transform-origin: top left;
    margin-top: -2 * variables.$fake-zoom * 1px; // adjust for border
    margin-left: -2 * variables.$fake-zoom * 1px;
    border: 1px solid transparent; // to avoid border not being drawn when width or height is 0
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.faster-fade-enter-active,
.faster-fade-leave-active {
  transition: opacity 0.15s;
}

.fade-enter-from,
.fade-leave-to,
.faster-fade-enter-from,
.faster-fade-leave-to {
  opacity: 0;
}

.board {
  background-color: colors.$background-ui;
  position: absolute;
  width: variables.$fake-zoom * 100%;
  height: variables.$fake-zoom * 100%;
  font-size: variables.$fake-zoom * 100%;

  &.pan-mode {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  // all sizes inside .backdrop should be measured in board.len
  .backdrop {
    position: absolute;
    height: 100%;
    width: 100%;
    overflow: hidden;
    /* stylelint-disable-next-line */
    font-size: variables.$horizontal-board-font-size + 0vw;

    .high-contrast-mode & {
      border: board.len(2px) solid $board-border;
    }

    h1,
    .h1 {
      font-weight: font.$weight-black;
      font-size: board.len(180px);
      color: colors.$text;
      text-align: center;
      margin: 0;
    }

    h2,
    .h2 {
      font-weight: font.$weight-extra-light;
      font-size: board.len(60px);
      text-align: center;
      margin: 0;
    }

    h3,
    .h3 {
      font-weight: font.$weight-black;
      font-size: board.len(22px);
      margin: 0;
      overflow: hidden;
    }

    h4,
    .h4 {
      font-weight: font.$weight-bold;
      font-size: board.len(14px);
      margin: 0;
      overflow: hidden;
    }
  }

  @media (aspect-ratio >= 16/9) {
    .backdrop {
      /* stylelint-disable-next-line */
      font-size: variables.$vertical-board-font-size + 0vh;
    }
  }

  .field {
    position: absolute;

    &.right {
      text-align: right;
    }
  }
}

a,
a:visited {
  color: inherit;
}

.scrollable {
  position: absolute;
  inset: 0;
  overflow: hidden auto;

  &.scrollable-relative {
    position: relative;
  }
}

.item {
  text-align: left;
  overflow: hidden;
  cursor: pointer;
}

.board-elem:focus-within,
.item.hoverable:hover,
.item.current {
  background-color: colors.$background-alpha-hover;
}

.item.selected {
  font-weight: font.$weight-extra-bold;

  input {
    font-weight: inherit;
  }
}
</style>
